import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CButton, CCard, CCardBody, CModal, CModalBody } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { toast } from 'react-toastify';

import { setFlexibleMultiModal } from '../../../../../../actions/common';
import { callTokenApi } from '../../../../../../apiCaller';
import {
     AI_INSIGHTS_TEMPLATE_STATUS,
     API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE,
     API_AI_INSIGHTS_TEMPLATES_BY_ID,
     API_AI_INSIGHTS_TEMPLATES_BY_SAVE_REPORT,
     COMPONENT_NAME,
} from '../../../../../../constants';
import { toastError } from '../../../../../../utils';
import { ScheduleContext } from '..';
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import { ConfirmRemovePopup, ConfirmSaveChange } from '../../../../../general/popup';
import ViewTemplateCard from './templateCard';
import { fetchSavedReportsRequest } from '../../../../../../actions/subscriber';
import { foreignTemplateMessage, isForeignTemplate } from '../../../../../../utils/aiInsightTemplates';
import _ from 'lodash';

const ManageTemplate = () => {
     const { showAiTemplate, setShowAiTemplate, reportData, setReportData, models } = useContext(ScheduleContext);

     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const user = useSelector((state) => state.theme.user);
     const [viewTemplates, setViewTemplates] = useState([]);
     const [refreshTemplate, setReFreshTemplate] = useState(false);
     const [isLoading, setIsLoading] = useState(false);
     const [show, setShow] = useState(false);
     const [saveLoading, setSaveLoading] = useState(false);
     const [data, setData] = useState({});
     const [showToggle, setShowToggle] = useState(false);
     const dispatch = useDispatch();

     useEffect(() => {
          document.body.classList.add('modal-open-schedule');
     }, []); // eslint-disable-line

     const loadData = useCallback(async () => {
          setIsLoading(true);
          try {
               const templatesResponse = await callTokenApi(
                    `${API_AI_INSIGHTS_TEMPLATES_BY_SAVE_REPORT.replace(':saveReportId', reportData.id)}`,
                    'GET'
               );
               if (templatesResponse.status === 200) {
                    const newData = templatesResponse.data.data;
                    setViewTemplates((prev) => {
                         if (_.isEqual(prev, newData)) {
                              return prev;
                         }
                         return newData;
                    });
                    setReFreshTemplate(false);
               } else {
                    toastError(templatesResponse);
               }
          } catch (error) {
               console.error('Fetch data error:', error);
               toast.error('An error occurred while loading data.');
          } finally {
               setIsLoading(false);
          }
     }, [reportData.id]);

     useEffect(() => {
          if (reportData?.id || refreshTemplate )loadData();
     }, [loadData, reportData?.id, refreshTemplate]);

     const handleClose = useCallback(() => {
          document.body.classList.remove('modal-open-schedule');
          setShowAiTemplate(false);
          setReportData(null);
     }, [setShowAiTemplate, setReportData]);

     const handleCreateEdit = useCallback(
          (id) => {
               dispatch(
                    setFlexibleMultiModal({
                         show: true,
                         component: COMPONENT_NAME.AI_INSIGHTS_TEMPLATE,
                         attributes: {
                              ruleId: id,
                              viewTemplates,
                              setViewTemplates,
                              models,
                              setReFreshTemplate,
                         },
                    })
               );
          },
          [dispatch, viewTemplates, setViewTemplates, models]
     );

     const handleDeleteTemplate = useCallback(
          (id) => {
               if (!id) return;
               setSaveLoading(true);
               callTokenApi(`${API_AI_INSIGHTS_TEMPLATES_BY_ID.replace(':id', id)}`, 'DELETE')
                    .then((response) => {
                         if (response.status === 200) {
                              setViewTemplates((prev) => prev.filter((tpl) => tpl.id !== id));
                              toast.success('Template deleted successfully.');
                              dispatch(fetchSavedReportsRequest(activeAccount.id, user.id));
                         }
                    })
                    .catch((error) => {
                         console.error('Delete template error:', error);
                         toast.error('Failed to delete template. Please try again later.');
                    })
                    .finally(() => {
                         setShow(false);
                         setSaveLoading(false);
                    });
          },
          [dispatch, activeAccount.id, user.id, setViewTemplates]
     );

     const onToggle = useCallback((id, status) => {
          if (!id) return;
          setSaveLoading(true);
          callTokenApi(`${API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE}/${id}`, 'PUT', { status })
               .then((response) => {
                    if (response.status === 200) {
                         toast.success(`Template ${status} updated successfully.`);
                         setViewTemplates((prev) => prev.map((tpl) => (tpl.id === id ? { ...tpl, status } : tpl)));
                    } else {
                         toastError(response);
                    }
               })
               .catch((error) => {
                    console.error('Update template error:', error);
                    toast.error('Failed to update template. Please try again later.');
               })
               .finally(() => {
                    setShowToggle(false);
                    setSaveLoading(false);
               });
     }, []);

     return (
          <>
               <div className="form-categories schedule-saved-report goals manager-template">
                    <CModal
                         className="schedule-modal"
                         visible={showAiTemplate}
                         alignment="center"
                         onClose={handleClose}
                         color="primary"
                         portal={false}
                         backdrop="static"
                    >
                         <CModalBody className="goalsContent">
                              <CCard className="card form-categories--card form-categories--categorizing">
                                   <CIcon icon="cil-x" onClick={handleClose} className="icon-close-popup" />
                                   <CCardBody>
                                        <div className="title">
                                             <h3>AI Insights Templates</h3>
                                             <p>This report is used in the following AI Insights Templates</p>
                                        </div>
                                        <div className="form-actions text-center">
                                             <div className={`list-rules  ${!isLoading ? 'list-rules-data' : ''}`}>
                                                  {isLoading ? (
                                                       <CenterSpinner />
                                                  ) : viewTemplates.length > 0 ? (
                                                       viewTemplates.map((viewTemplate, index) => (
                                                            <ViewTemplateCard
                                                                 key={viewTemplate.id || index}
                                                                 index={index}
                                                                 viewReport={viewTemplate}
                                                                 setViewTemplates={setViewTemplates}
                                                                 onToggle={() => {
                                                                      if (isForeignTemplate(viewTemplate, activeAccount.id)) {
                                                                           toast.error(foreignTemplateMessage(viewTemplate));
                                                                           return;
                                                                      }
                                                                      setShowToggle(true);
                                                                      setData(viewTemplate);
                                                                 }}
                                                                 onEditTemplate={() => {
                                                                      if (isForeignTemplate(viewTemplate, activeAccount.id)) {
                                                                           toast.error(foreignTemplateMessage(viewTemplate));
                                                                           return;
                                                                      }
                                                                      handleCreateEdit(viewTemplate.id);
                                                                 }}
                                                                 onDeleteTemplate={() => {
                                                                      if (isForeignTemplate(viewTemplate, activeAccount.id)) {
                                                                           toast.error(foreignTemplateMessage(viewTemplate));
                                                                           return;
                                                                      }
                                                                      setShow(true);
                                                                      setData(viewTemplate);
                                                                 }}
                                                            />
                                                       ))
                                                  ) : (
                                                       <p>No AI Insights Templates use this report.</p>
                                                  )}
                                             </div>
                                             <CButton type="button" color="primary" className="mr-3 mt-4" onClick={() => handleCreateEdit()}>
                                                  Create AI Insights Template
                                             </CButton>
                                        </div>
                                   </CCardBody>
                              </CCard>
                         </CModalBody>
                    </CModal>
               </div>

               <ConfirmRemovePopup
                    show={show}
                    onAccept={() => handleDeleteTemplate(data.id)}
                    onClose={() => {
                         setShow(false);
                         setData({});
                    }}
                    title="Delete this Template?"
                    isLoading={saveLoading}
                    className="modal-lv2"
               >
                    <p>You are about to delete this Template. Please confirm.</p>
               </ConfirmRemovePopup>

               {showToggle && (
                    <ConfirmSaveChange
                         show={showToggle}
                         onAccept={() =>
                              onToggle(
                                   data.id,
                                   data.status === AI_INSIGHTS_TEMPLATE_STATUS.ACTIVE
                                        ? AI_INSIGHTS_TEMPLATE_STATUS.INACTIVE
                                        : AI_INSIGHTS_TEMPLATE_STATUS.ACTIVE
                              )
                         }
                         onClose={() => {
                              setShowToggle(false);
                              setData({});
                         }}
                         isLoading={saveLoading}
                         title="Are you sure?"
                         className="modal-lv2"
                    >
                         <p>
                              You are about to{' '}
                              {data?.status === AI_INSIGHTS_TEMPLATE_STATUS.ACTIVE
                                   ? AI_INSIGHTS_TEMPLATE_STATUS.INACTIVE
                                   : AI_INSIGHTS_TEMPLATE_STATUS.ACTIVE}{' '}
                              the schedule <strong>"{data ? data.name : ''}"</strong>.
                         </p>
                    </ConfirmSaveChange>
               )}
          </>
     );
};

export default ManageTemplate;
